import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Trophy, Check, Lock, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";
import { MetricsGrid } from "@/components/c2g/ReportEngine";
import { DAY_STATUS, STATUS_LABEL } from "@/components/c2g/labels";

export default function Statistiche() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  if (!s) return <Loader />;
  if (s.engine) {
    return (
      <AppShell title="Statistiche" greeting={`Settimana ${s.week} · dati confermati`}>
        <div className="px-5 pt-6" data-testid="c2g-stats-page">
          <MetricsGrid stats={s.stats} testid="c2g-stats" />
          <p className="mt-3 text-sm text-[#8A8E96]">Giorni saltati: <span className="tabular-nums font-semibold text-[#D4D4D8]" data-testid="c2g-stats-skipped">{s.stats.skipped}</span></p>
          <p className="mt-1 text-sm text-[#8A8E96]">Un giorno è "concluso" quando hai scelto il passo al Mattino e chiuso la giornata alla Sera, anche se il passo non è stato svolto. Letture ed esercizi non cambiano questi numeri.</p>
          <ul className="mt-5 space-y-2">
            {s.per_day.map((d) => (
              <li key={d.day} className="flex justify-between rounded-xl border border-[#27272A] bg-[#121215] p-3 text-sm" data-testid={`c2g-stats-day-${d.day}`}>
                <span className="font-semibold text-[#E5C158]">Giorno {d.day}</span>
                <span className="text-[#B4B4BC]">{DAY_STATUS[d.day_status]} · {STATUS_LABEL[d.status]}</span>
              </li>
            ))}
          </ul>
        </div>
      </AppShell>
    );
  }
  const pct = Math.round((s.completed_count / s.total_days) * 100);

  return (
    <AppShell title="Statistiche" greeting="Progressi e traguardi">
      <div className="px-5 pt-6">
        <div className="grid grid-cols-2 gap-3">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] p-5">
            <Flame size={18} className="text-[#D4AF37]" />
            <p className="tabular-nums mt-2 text-3xl font-semibold text-[#FAF9F6]" data-testid="stat-lived">{s.lived}/7</p>
            <p className="text-[12px] uppercase tracking-wider text-[#8A8E96]">Giorni vissuti</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
            <RotateCcw size={18} className="text-[#D4AF37]" />
            <p className="tabular-nums mt-2 text-3xl font-semibold text-[#FAF9F6]" data-testid="stat-restarts">{s.restarts}</p>
            <p className="text-[12px] uppercase tracking-wider text-[#8A8E96]">Ripartenze</p>
          </motion.div>
        </div>
        <p className="mt-2 text-sm text-[#8A8E96]">Saltare un giorno non azzera niente: ogni volta che riprendi, conta.</p>

        <div className="mt-4 rounded-2xl border border-[#27272A] bg-[#121215] p-5" data-testid="stat-actions">
          <p className="eyebrow">I tuoi passi</p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[["Completati", s.actions_done, "stat-actions-done"], ["Completati ridotti", s.actions_reduced, "stat-actions-reduced"], ["Non svolti", s.actions_not, "stat-actions-not"]].map(([l, v, t]) => (
              <div key={t} className="rounded-xl bg-[#0D0D0F] p-3">
                <p className="tabular-nums text-2xl font-semibold text-[#FAF9F6]" data-testid={t}>{v}</p>
                <p className="text-[12px] uppercase tracking-wider text-[#8A8E96]">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#8A8E96]">Conta solo ciò che hai confermato tu. In attesa di conferma: <span className="tabular-nums font-semibold text-[#D4D4D8]" data-testid="stat-actions-pending">{s.actions_pending}</span> · Giorni con interazione: <span className="tabular-nums font-semibold text-[#D4D4D8]" data-testid="stat-active-days">{s.active_days}</span></p>
          <p className="mt-1 text-sm text-[#8A8E96]">Un giorno è "vissuto" quando hai scelto il passo al Mattino e chiuso la giornata alla Sera, anche se il passo non è riuscito.</p>
        </div>

        <div className="mt-4 rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Giorni vissuti</p>
            <span className="tabular-nums font-semibold text-base text-[#D4D4D8]">{s.completed_count}/{s.total_days} giorni · {pct}%</span>
          </div>
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#27272A]">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-[#E5C158] to-[#997A20]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} />
          </div>
          <div className="mt-4 flex justify-between">
            {s.per_day.map((d) => (
              <div key={d.day} className="flex flex-col items-center gap-1.5">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
                  style={d.completed ? { backgroundColor: "rgba(16,185,129,0.15)", color: "#10B981" } : { backgroundColor: "#18181C", color: "#8A8E96" }}
                >
                  {d.completed ? <Check size={14} /> : d.day}
                </span>
                <span className="tabular-nums font-semibold text-[12px] text-[#8A8E96]">G{d.day}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-[#8A8E96]">Momenti completati: <span className="tabular-nums font-semibold text-[#D4D4D8]">{s.moments_done}/{s.total_moments}</span></p>
        </div>

        <p className="eyebrow mt-7">Traguardi</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {s.badges.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              className={`rounded-2xl border p-4 ${b.unlocked ?"border-[#D4AF37]/40 bg-[#D4AF37]/8" : "border-[#27272A] bg-[#121215] opacity-70"}`}
              data-testid={`badge-${b.id}`}
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${b.unlocked ?"bg-gradient-to-br from-[#E5C158] to-[#997A20] text-[#09090B]" : "bg-[#18181C] text-[#8A8E96]"}`}>
                {b.unlocked ? <Trophy size={18} /> : <Lock size={16} />}
              </div>
              <p className="mt-2.5 text-base text-[#F4F4F5] font-semibold">{b.label}</p>
              <p className="mt-0.5 text-sm text-[#8A8E96]">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
